/* assets/rod-collection.js : the shared product-listing UI (WP-4, SPEC 5.9 / 3.5).
   Bound to every [data-rod-plp] root, context-agnostic: the same code runs on collection pages
   and on the search results page (WP-3). Everything it does is an upgrade of markup that already
   works without JavaScript - the facet form submits, the sort form has a <noscript> button and
   every pagination, category and chip link is a real href. */
import { Rod } from '@rod/core';

const doc = document;
const ANCHOR = '#urunler';
const COUNT_DEBOUNCE = 300;
// One backoff before a throttled (429) staged-count request is given up on; see refreshCount().
const COUNT_RETRY_MS = 700;

/* ------------------------------------------------------------------ helpers */

const toUrl = (href) => new URL(href, location.href);

/**
 * Stable comparison key: same path and same parameter set, order-independent.
 * `page` never takes part: a staged drawer url has already dropped it, so keeping it on the other
 * side made "Apply with nothing changed" on page 2 look like a change and navigate to page 1.
 */
function canon(url) {
  const pairs = [];
  url.searchParams.forEach((v, k) => {
    if (k !== 'page') pairs.push(`${k}=${v}`);
  });
  pairs.sort();
  return `${url.origin}${url.pathname}${pairs.length ? `?${pairs.join('&')}` : ''}`;
}

/** The key `sections/rod-collection-count.liquid` uses for a filter value: `param_name=value`. */
function factKey(box) {
  return `${box.name}=${box.value}`;
}

/** The text a facet row is matched against by the "Marka ara" box. */
function rowText(row) {
  const label = row.querySelector('.rod-fct__label');
  return (label ? label.textContent : row.textContent) || '';
}

function withAnchor(href) {
  return href.includes('#') ? href : href + ANCHOR;
}

/**
 * Turkish-friendly number input: "1.234,56" and "1234.56" both parse.
 * A comma means the decimal separator, so dots are thousands separators in that case.
 */
function parseAmount(raw) {
  if (raw == null) return null;
  let s = String(raw).trim().replace(/\s/g, '');
  if (!s) return null;
  if (s.includes(',')) s = s.replace(/\./g, '').replace(',', '.');
  const n = Number(s);
  if (!Number.isFinite(n)) return null;
  if (n > 99999999) return 99999999;
  return n;
}

function formatAmount(n) {
  const r = Math.round(n * 100) / 100;
  return String(r);
}

function announceCount(n) {
  Rod.announce(`${n} ürün`);
}

/**
 * The server cannot know the stored view preference, so it renders the default (list) pressed
 * state. This runs at module evaluation - after the document is parsed, before DOMContentLoaded -
 * so the buttons agree with the pre-paint `data-rod-plp-view` attribute from the first frame on.
 */
function syncViewButtons() {
  const current = doc.documentElement.getAttribute('data-rod-plp-view') === 'grid' ? 'grid' : 'list';
  for (const b of doc.querySelectorAll('[data-rod-view]')) {
    b.setAttribute('aria-pressed', String(b.dataset.rodView === current));
  }
}

/* ------------------------------------------------------------------ PLP root */

function initPlp(root) {
  if (root.dataset.rodPlpBound === '1') return;
  root.dataset.rodPlpBound = '1';

  const baseUrl = root.dataset.rodBaseUrl || location.pathname;
  const countSection = root.dataset.rodCountSection || 'rod-collection-count';
  /* 'collection' | 'search' (SPEC 3.5). Search is the only context whose per-value counts Shopify
     cannot honour, so the staged counts are applied to the footer only - see applyCount(). */
  const isSearch = root.dataset.rodPlpContext === 'search';
  const main = root.querySelector('.rod-plp__main');
  const progress = root.querySelector('[data-rod-progress]');
  const sideForm = root.querySelector('.rod-fct--sidebar');
  const drawer = root.querySelector('[data-rod-filter-drawer]');
  const sheet = root.querySelector('[data-rod-sort-sheet]');
  /* Built from the sidebar form the first time the drawer opens, so the page ships ONE copy of the
     facet markup instead of two (SPEC T-PERF-05). */
  let drawerForm = null;
  let drawerTrigger = null;
  let sheetTrigger = null;

  /* ---------------- busy state ---------------- */
  function startBusy() {
    if (main) main.setAttribute('aria-busy', 'true');
    if (progress) progress.hidden = false;
  }
  function stopBusy() {
    if (main) main.removeAttribute('aria-busy');
    if (progress) progress.hidden = true;
  }
  function go(href) {
    startBusy();
    Rod.overlay.navigate(withAnchor(href));
  }
  window.addEventListener('pageshow', (e) => {
    if (e.persisted) stopBusy();
  });

  /* ---------------- stuck state of the Filtrele/Sırala bar ---------------- */

  const bar = root.querySelector('[data-rod-plp-bar]');
  const sentinel = root.querySelector('[data-rod-bar-sentinel]');
  if (bar && sentinel && 'IntersectionObserver' in window) {
    const watchStuck = () => {
      const top = parseFloat(getComputedStyle(bar).top);
      if (!Number.isFinite(top)) return;
      if (watchStuck.observer) watchStuck.observer.disconnect();
      watchStuck.observer = new IntersectionObserver(
        ([entry]) => bar.classList.toggle('is-stuck', !entry.isIntersecting),
        { rootMargin: `-${Math.max(0, Math.round(top))}px 0px 0px 0px`, threshold: 0 }
      );
      watchStuck.observer.observe(sentinel);
    };
    watchStuck();
    window.addEventListener('resize', Rod.debounce(watchStuck, 200));
  }

  /* ---------------- staged / target url from a facet form ---------------- */

  /**
   * The url a facet form currently describes. The form IS the state: brand checkboxes decide the
   * destination collection (SPEC 5.9.4 brand logic), everything else becomes a query parameter.
   */
  function formUrl(form) {
    const brands = [...form.querySelectorAll('.rod-fct__brand')];
    const checked = brands.filter((b) => b.checked);
    const url = toUrl(checked.length === 1 ? checked[0].dataset.url : baseUrl);

    // Only the identity parameters of the base url survive (q/type on /search).
    for (const key of ['sort_by', 'page']) url.searchParams.delete(key);

    const sort = form.querySelector('input[name="sort_by"]');
    if (sort && sort.value) url.searchParams.set('sort_by', sort.value);

    if (checked.length >= 2) {
      for (const b of checked) url.searchParams.append('filter.p.vendor', b.dataset.vendor || b.value);
    }
    for (const box of form.querySelectorAll('.rod-fct__value')) {
      if (box.checked && !box.disabled) url.searchParams.append(box.name, box.value);
    }

    const price = readPrice(form);
    const minEl = form.querySelector('[data-rod-price-min]');
    const maxEl = form.querySelector('[data-rod-price-max]');
    if (minEl) {
      url.searchParams.delete(minEl.name);
      if (price.min != null) url.searchParams.set(minEl.name, formatAmount(price.min));
    }
    if (maxEl) {
      url.searchParams.delete(maxEl.name);
      if (price.max != null) url.searchParams.set(maxEl.name, formatAmount(price.max));
    }
    return url;
  }

  /**
   * Reads, normalises and (when needed) swaps the two price inputs.
   * `notify` is passed only by the handlers the user triggers (blur, Enter, Uygula): the count
   * refresh calls this too, and it must not wipe the swap note it finds already normalised.
   */
  function readPrice(form, { notify = false } = {}) {
    const minEl = form.querySelector('[data-rod-price-min]');
    const maxEl = form.querySelector('[data-rod-price-max]');
    const msg = form.querySelector('.rod-fct__msg');
    const minRaw = minEl ? minEl.value.trim() : '';
    const maxRaw = maxEl ? maxEl.value.trim() : '';
    let min = parseAmount(minRaw);
    let max = parseAmount(maxRaw);
    // Text that is not a number is dropped from the url, so it must be called out: otherwise the
    // shopper keeps reading "abc" in "En az" next to a count that ignores it (SPEC 3.2 item 7).
    const badMin = minRaw !== '' && min == null;
    const badMax = maxRaw !== '' && max == null;
    if (minEl) minEl.setAttribute('aria-invalid', badMin ? 'true' : 'false');
    if (maxEl) maxEl.setAttribute('aria-invalid', badMax ? 'true' : 'false');
    if (min != null && min < 0) min = 0;
    if (max != null && max < 0) max = 0;
    let swapped = false;
    if (min != null && max != null && min > max) {
      const t = min;
      min = max;
      max = t;
      swapped = true;
    }
    if (minEl && min != null) minEl.value = formatAmount(min);
    if (maxEl && max != null) maxEl.value = formatAmount(max);
    if (msg && (notify || swapped || badMin || badMax)) {
      if (badMin || badMax) msg.textContent = 'Lütfen sayı girin.';
      else msg.textContent = swapped ? 'En az ve en çok değerleri yer değiştirdi.' : '';
    }
    return { min, max };
  }

  // Typing again retires the note.
  function bindPriceInputs(scope) {
    for (const input of scope.querySelectorAll('[data-rod-price-min], [data-rod-price-max]')) {
      input.addEventListener('input', () => {
        const wrap = input.closest('.rod-fct');
        const msg = wrap && wrap.querySelector('.rod-fct__msg');
        if (msg) msg.textContent = '';
        input.setAttribute('aria-invalid', 'false');
      });
    }
  }
  bindPriceInputs(root);

  /* ---------------- sidebar: navigate straight away ---------------- */

  if (sideForm) {
    sideForm.addEventListener('change', (e) => {
      const el = e.target;
      if (!(el instanceof HTMLInputElement)) return;
      if (el.classList.contains('rod-fct__brand') || el.classList.contains('rod-fct__value')) {
        go(formUrl(sideForm).href);
      }
    });
    sideForm.addEventListener('submit', (e) => {
      e.preventDefault();
      readPrice(sideForm, { notify: true });
      go(formUrl(sideForm).href);
    });
    for (const input of sideForm.querySelectorAll('[data-rod-price-min], [data-rod-price-max]')) {
      input.addEventListener('blur', () => readPrice(sideForm, { notify: true }));
    }
  }

  /* ---------------- sort select ---------------- */

  /* WCAG 3.2.2 (On Input): a closed <select> fires `change` on every arrow key in Chrome, so
     submitting on `change` navigated the page after a single ArrowDown and forced the shopper to
     tab back for each further step. Pointer choices still submit immediately; keyboard choices wait
     for an explicit commit (Enter, blur, or the "Uygula" button that appears on the first key). */
  const sortSelect = root.querySelector('[data-rod-sort-select]');
  if (sortSelect) {
    const sortForm = sortSelect.closest('form');
    const sortGo = sortForm && sortForm.querySelector('[data-rod-sort-go]');
    let byKeyboard = false;
    let committedValue = sortSelect.value;

    const submitSort = () => {
      if (!sortForm) return;
      const url = toUrl(sortForm.getAttribute('action') || location.pathname);
      url.search = '';
      const data = new FormData(sortForm);
      for (const [k, v] of data.entries()) {
        if (v !== '') url.searchParams.append(k, String(v));
      }
      committedValue = sortSelect.value;
      go(url.href);
    };

    sortSelect.addEventListener('pointerdown', () => {
      byKeyboard = false;
    });
    sortSelect.addEventListener('keydown', (e) => {
      if (e.key === 'Tab') return;
      byKeyboard = true;
      if (sortGo) sortGo.hidden = false;
      if (e.key === 'Enter') {
        e.preventDefault();
        if (sortSelect.value !== committedValue) submitSort();
      }
      if (e.key === 'Escape') sortSelect.value = committedValue;
    });
    sortSelect.addEventListener('change', () => {
      if (!byKeyboard) submitSort();
    });
    sortSelect.addEventListener('blur', () => {
      if (byKeyboard && sortSelect.value !== committedValue) submitSort();
    });
    if (sortForm) {
      sortForm.addEventListener('submit', (e) => {
        e.preventDefault();
        submitSort();
      });
    }
  }

  /* ---------------- progress feedback on every filter link ---------------- */

  root.addEventListener('click', (e) => {
    const link = e.target instanceof Element ? e.target.closest('a[href]') : null;
    if (!link || e.defaultPrevented || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    if (link.closest('.rod-fct, .rod-chips--active, .rod-pg')) startBusy();
  });

  /* ---------------- brand search ---------------- */

  function bindBrandSearch(scope) {
    for (const input of scope.querySelectorAll('[data-rod-brand-search]')) {
      bindOneBrandSearch(input);
    }
  }

  function bindOneBrandSearch(input) {
    const body = input.closest('.rod-fct__body');
    if (!body) return;
    const list = body.querySelector('[data-rod-brand-list]');
    const none = body.querySelector('[data-rod-brand-none]');
    const rows = list ? [...list.querySelectorAll('[data-rod-brand-row]')] : [];
    const folded = rows.map((r) => Rod.fold(rowText(r).trim()));
    const filter = () => {
      const q = Rod.fold(input.value.trim());
      if (!q) {
        for (const r of rows) r.hidden = false;
        if (none) none.hidden = true;
        return;
      }
      let hits = 0;
      rows.forEach((r, i) => {
        const hit = folded[i].includes(q);
        r.hidden = !hit;
        if (hit) hits += 1;
      });
      if (none) none.hidden = hits > 0;
    };
    // Closing and reopening the drawer resets the form but not this client-side filter, so the
    // reset path below needs to be able to run it again (SPEC 5.9.8 "Reopen shows applied state").
    input.rodFilter = filter;
    input.addEventListener('input', Rod.debounce(filter, 120));
  }
  bindBrandSearch(root);

  /* ---------------- group collapse memory (sidebar) ---------------- */

  if (sideForm) {
    // Includes the "Seçili Filtreler" group, which sits next to the form rather than inside it.
    const sideGroups = [...root.querySelectorAll('[data-rod-fct-group]')].filter((g) => !drawer || !drawer.contains(g));
    for (const g of sideGroups) {
      const key = `rod:fct:${g.dataset.rodFctGroup}`;
      const saved = Rod.session.get(key, null);
      if (saved === false) g.open = false;
      g.addEventListener('toggle', () => Rod.session.set(key, g.open));
    }
  }

  /* ---------------- filter drawer ---------------- */

  let countSeq = 0;
  let countAbort = null;

  function applyCount(data) {
    const n = Number(data && data.count);
    const count = Number.isFinite(n) ? n : 0;
    const label = drawer && drawer.querySelector('[data-rod-drawer-count]');
    const apply = drawer && drawer.querySelector('[data-rod-filter-apply]');
    const applyText = drawer && drawer.querySelector('[data-rod-apply-text]');
    if (label) label.textContent = `${count} ürün`;
    if (applyText) applyText.textContent = count === 0 ? 'Sonuç yok' : `${count} Ürünü Göster`;
    if (apply) apply.disabled = count === 0;

    const values = (data && data.values) || {};
    if (!drawerForm) return;
    /* T-PLP-29: in SEARCH context Shopify's own `filter.values[].count` is computed against a
       results_count the index cannot serve (q=kalem: Bigpoint promised 344, delivered 262;
       filter.v.availability=1 promised 470 of 470 while 64 values are out of stock). The rows
       therefore carry no count on search (rod-facets `fct_show_counts`) and nothing here may write
       one back or disable a row from those numbers. The footer count above IS re-fetched for the
       staged url and does match the destination page, so it stays. */
    if (isSearch) return;
    /* Brand rows are in here too (SPEC 5.9.8: "each value's count"): their key is the same
       `filter.p.vendor=<vendor>` pair the count section emits, so their counts no longer stay at the
       unfiltered number while other filters are staged. A checked value is never disabled - with
       exactly one brand checked the staged url IS that brand collection, and the response then
       carries only that one vendor, so the other brands simply keep their server counts. */
    for (const box of drawerForm.querySelectorAll('.rod-fct__box')) {
      const key = factKey(box);
      if (!(key in values)) continue;
      const c = Number(values[key]) || 0;
      const row = box.closest('.rod-fct__row');
      const out = row && row.querySelector('.rod-fct__count');
      if (out) out.textContent = `(${c})`;
      const off = c === 0 && !box.checked;
      box.disabled = off;
      if (row) row.classList.toggle('is-off', off);
    }
  }

  const announceLatest = Rod.debounce(announceCount, 500);

  async function refreshCount() {
    if (!drawerForm) return;
    const url = toUrl(formUrl(drawerForm).href);
    url.searchParams.set('section_id', countSection);
    const seq = ++countSeq;
    if (countAbort) countAbort.abort();
    countAbort = new AbortController();
    try {
      /* The Section Rendering API answers 429 under rapid staged changes. Without a retry the
         footer silently keeps the previous number and "{N} Ürünü Göster" lies about the result
         (SPEC 5.9.8). One backoff retry is enough for the observed throttling; a still-failing
         response keeps the last known count rather than showing a wrong one. */
      let res = await fetch(url.href, { signal: countAbort.signal, headers: { Accept: 'text/html' } });
      if (!res.ok && res.status === 429) {
        await new Promise((r) => setTimeout(r, COUNT_RETRY_MS));
        if (seq !== countSeq) return;
        res = await fetch(url.href, { signal: countAbort.signal, headers: { Accept: 'text/html' } });
      }
      if (!res.ok) return;
      const html = await res.text();
      if (seq !== countSeq) return;
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const node = parsed.querySelector('#rod-count');
      if (!node) return;
      const data = JSON.parse(node.textContent || '{}');
      applyCount(data);
      announceLatest(Number(data.count) || 0);
    } catch (err) {
      if (err && err.name !== 'AbortError') console.warn('[rod] filter count failed', err);
    }
  }

  const scheduleCount = Rod.debounce(refreshCount, COUNT_DEBOUNCE);

  function closeDrawerDialog() {
    if (!drawer) return;
    try {
      drawer.close();
    } catch (e) {
      drawer.removeAttribute('open');
    }
  }

  /**
   * The drawer body is empty in the server HTML (SPEC T-PERF-05). The first open clones the sidebar
   * facet form into it - same markup, same server state, no second copy on the wire and no fetch.
   * Ids are re-namespaced so the two forms never collide.
   */
  function buildDrawerForm() {
    if (drawerForm || !drawer) return drawerForm;
    // A consumer that still renders the facets into the drawer server-side keeps working.
    const served = drawer.querySelector('.rod-fct--drawer');
    if (served) {
      drawerForm = served;
      markLimitable(served);
      bindDrawerForm(served);
      bindPriceInputs(served);
      bindBrandSearch(served);
      return drawerForm;
    }
    const body = drawer.querySelector('[data-rod-drawer-body]');
    if (!body || !sideForm) return null;
    const clone = sideForm.cloneNode(true);
    clone.classList.remove('rod-fct--sidebar');
    clone.classList.add('rod-fct--drawer');
    clone.dataset.rodFacetsMode = 'drawer';
    // The <symbol> sprite stays a single instance in the document; the clone keeps using <use>.
    for (const el of clone.querySelectorAll('.rod-fct__defs')) el.remove();
    const swap = (s) => s.replace('-sidebar-', '-drawer-');
    for (const el of clone.querySelectorAll('[id]')) el.id = swap(el.id);
    for (const el of clone.querySelectorAll('[aria-describedby]')) {
      el.setAttribute('aria-describedby', swap(el.getAttribute('aria-describedby')));
    }
    for (const el of clone.querySelectorAll('[for]')) el.setAttribute('for', swap(el.getAttribute('for')));
    body.appendChild(clone);
    drawerForm = clone;
    bindDrawerForm(clone);
    bindPriceInputs(clone);
    bindBrandSearch(clone);
    return drawerForm;
  }

  /* SPEC 5.9.8: closing without applying discards staged controls, brand search and group state. */
  function resetDrawerUi(form) {
    form.reset();
    for (const box of form.querySelectorAll('.rod-fct__box')) box.disabled = false;
    for (const row of form.querySelectorAll('.rod-fct__row')) row.classList.remove('is-off');
    for (const msg of form.querySelectorAll('.rod-fct__msg')) msg.textContent = '';
    for (const input of form.querySelectorAll('[data-rod-price-min], [data-rod-price-max]')) {
      input.setAttribute('aria-invalid', 'false');
    }
    for (const row of form.querySelectorAll('[data-rod-brand-row]')) row.hidden = false;
    for (const none of form.querySelectorAll('[data-rod-brand-none]')) none.hidden = true;
    for (const input of form.querySelectorAll('[data-rod-brand-search]')) {
      input.value = '';
      if (typeof input.rodFilter === 'function') input.rodFilter();
    }
    applyGroupState(form);
  }

  /** Dialog triggers must report their state to assistive tech (SPEC 3.2 item 4). */
  function setExpanded(selector, open) {
    for (const btn of root.querySelectorAll(selector)) {
      btn.setAttribute('aria-expanded', open ? 'true' : 'false');
    }
  }

  function openDrawer(trigger = null) {
    if (!drawer) return;
    drawerTrigger = trigger;
    buildDrawerForm();
    if (drawerForm) resetDrawerUi(drawerForm);
    setExpanded('[data-rod-open-filter]', true);
    Rod.overlay.open('filter', { history: true, element: drawer, close: closeDrawerDialog });
    Rod.lockScroll(drawer);
    try {
      drawer.showModal();
    } catch (e) {
      drawer.setAttribute('open', '');
    }
    refreshCount();
  }

  /* Defensive guard for a consumer that renders `rod-filter-drawer` without a sidebar `rod-facets`
     (SPEC 3.5: the drawer body is filled from the sidebar copy). Without a source the drawer would
     open empty, stage nothing and never update its count, so the button is removed instead and
     "Sırala" takes the whole bar. */
  if (drawer && !sideForm && !drawer.querySelector('.rod-fct--drawer')) {
    for (const btn of root.querySelectorAll('[data-rod-open-filter]')) btn.hidden = true;
    if (bar) bar.classList.add('is-nofilter');
    drawer.remove();
  }

  if (drawer && drawer.isConnected) {
    drawer.addEventListener('close', () => {
      Rod.unlockScroll(drawer);
      setExpanded('[data-rod-open-filter]', false);
      Rod.overlay.close('filter', { silent: true });
      const trigger = drawerTrigger;
      drawerTrigger = null;
      requestAnimationFrame(() => trigger?.focus({ preventScroll: true }));
    });
    for (const btn of root.querySelectorAll('[data-rod-open-filter]')) {
      btn.addEventListener('click', () => openDrawer(btn));
    }
    for (const btn of drawer.querySelectorAll('[data-rod-close-filter]')) {
      btn.addEventListener('click', () => Rod.overlay.close('filter'));
    }
    const clear = drawer.querySelector('[data-rod-filter-clear]');
    if (clear) {
      clear.addEventListener('click', () => {
        if (!drawerForm) return;
        for (const box of drawerForm.querySelectorAll('input[type="checkbox"]')) {
          box.checked = false;
          box.disabled = false;
        }
        for (const input of drawerForm.querySelectorAll('[data-rod-price-min], [data-rod-price-max]')) {
          input.value = '';
          input.setAttribute('aria-invalid', 'false');
        }
        for (const row of drawerForm.querySelectorAll('.rod-fct__row')) row.classList.remove('is-off');
        const msg = drawerForm.querySelector('.rod-fct__msg');
        if (msg) msg.textContent = '';
        refreshCount();
      });
    }
    const apply = drawer.querySelector('[data-rod-filter-apply]');
    if (apply) {
      apply.addEventListener('click', () => {
        if (!drawerForm) return;
        const target = formUrl(drawerForm);
        // `canon` ignores `page`, so applying an unchanged drawer on page 2 closes and scrolls
        // instead of throwing the shopper back to page 1 (SPEC 5.9.8).
        if (canon(target) === canon(toUrl(location.href))) {
          Rod.overlay.close('filter');
          const results = doc.getElementById('urunler');
          if (results) results.scrollIntoView({ block: 'start' });
          return;
        }
        startBusy();
        Rod.overlay.navigate(withAnchor(target.href));
      });
    }
  }

  /** SPEC 5.9.8: groups with active values and the first two groups are open, the rest closed. */
  function applyGroupState(form) {
    const groups = [...form.querySelectorAll('[data-rod-fct-group]')];
    groups.forEach((g, i) => {
      const hasActive = !!g.querySelector('input[type="checkbox"]:checked, .rod-fct__row--link.is-current');
      const hasPrice = !!g.querySelector('[data-rod-price-min][value]:not([value=""])');
      g.open = i < 2 || hasActive || hasPrice;
    });
  }

  /** Handlers of the cloned drawer form; called once, right after it is built. */
  function bindDrawerForm(form) {
    form.addEventListener('submit', (e) => {
      e.preventDefault();
      readPrice(form, { notify: true });
      refreshCount();
    });
    form.addEventListener('change', (e) => {
      const el = e.target;
      if (!(el instanceof HTMLInputElement)) return;
      if (el.type === 'checkbox') scheduleCount();
    });
    for (const input of form.querySelectorAll('[data-rod-price-min], [data-rod-price-max]')) {
      input.addEventListener('blur', () => {
        readPrice(form, { notify: true });
        scheduleCount();
      });
    }
    applyGroupState(form);
  }

  /* ---------------- sort sheet ---------------- */

  if (sheet) {
    const sheetGo = sheet.querySelector('[data-rod-sort-sheet-go]');
    /* WCAG 3.2.2 (On Input), the same rule the desktop <select> follows: an arrow key inside a radio
       group moves focus AND checks the radio, so a plain `change` listener navigated the page on the
       first ArrowDown and a keyboard user could never browse the options. Pointer taps and an
       explicit Enter/Space still go straight away; any other key reveals "Uygula" instead. */
    let sheetPointer = false;
    let sheetCommit = false;

    const closeSheet = () => {
      try {
        sheet.close();
      } catch (e) {
        sheet.removeAttribute('open');
      }
    };
    const goSheet = (radio) => {
      const el = radio || sheet.querySelector('input[name="rod-sort-option"]:checked');
      if (!el || !el.dataset.url) return;
      startBusy();
      Rod.overlay.navigate(withAnchor(el.dataset.url));
    };
    sheet.addEventListener('close', () => {
      Rod.unlockScroll(sheet);
      setExpanded('[data-rod-open-sort]', false);
      Rod.overlay.close('sort', { silent: true });
      const trigger = sheetTrigger;
      sheetTrigger = null;
      requestAnimationFrame(() => trigger?.focus({ preventScroll: true }));
    });
    for (const btn of root.querySelectorAll('[data-rod-open-sort]')) {
      btn.addEventListener('click', () => {
        sheetTrigger = btn;
        sheetPointer = false;
        sheetCommit = false;
        if (sheetGo) sheetGo.hidden = true;
        setExpanded('[data-rod-open-sort]', true);
        Rod.overlay.open('sort', { element: sheet, close: closeSheet });
        Rod.lockScroll(sheet);
        try {
          sheet.showModal();
        } catch (e) {
          sheet.setAttribute('open', '');
        }
      });
    }
    for (const btn of sheet.querySelectorAll('[data-rod-sort-close]')) {
      btn.addEventListener('click', () => Rod.overlay.close('sort'));
    }
    sheet.addEventListener('pointerdown', () => {
      sheetPointer = true;
      sheetCommit = false;
    });
    sheet.addEventListener('keydown', (e) => {
      if (e.key === 'Tab') return;
      sheetPointer = false;
      sheetCommit = e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar';
      if (e.key === 'Enter' && e.target instanceof HTMLInputElement && e.target.type === 'radio') {
        e.preventDefault();
        goSheet(e.target);
      }
    });
    sheet.addEventListener('change', (e) => {
      const el = e.target;
      if (!(el instanceof HTMLInputElement) || el.type !== 'radio' || !el.dataset.url) return;
      if (sheetPointer || sheetCommit) goSheet(el);
      else if (sheetGo) sheetGo.hidden = false;
    });
    if (sheetGo) sheetGo.addEventListener('click', () => goSheet(null));
    // Tapping the scrim above the sheet dismisses it, the way every mainstream mobile sheet does.
    sheet.addEventListener('click', (e) => {
      if (e.target === sheet) Rod.overlay.close('sort');
    });
  }

  /* ---------------- list / grid toggle (SPEC 5.6.2) ---------------- */

  const viewButtons = [...root.querySelectorAll('[data-rod-view]')];
  if (viewButtons.length) {
    const html = doc.documentElement;
    const sync = syncViewButtons;
    sync();
    for (const b of viewButtons) {
      b.addEventListener('click', () => {
        const value = b.dataset.rodView === 'grid' ? 'grid' : 'list';
        Rod.store.set('rod:plp:view', value);
        if (value === 'grid') html.setAttribute('data-rod-plp-view', 'grid');
        else html.removeAttribute('data-rod-plp-view');
        if (value === 'grid') {
          // A hidden stepper must never submit a stale quantity (SPEC 5.6.2).
          for (const input of root.querySelectorAll('.rod-card .rod-qty input')) input.value = '1';
        }
        sync();
        Rod.announce(value === 'grid' ? 'Izgara görünümü' : 'Liste görünümü');
      });
    }
  }
}

/* ------------------------------------------------------------------ read more / show more */

function initReadMore(wrap) {
  const body = wrap.querySelector('[data-rod-readmore-body]');
  const btn = wrap.querySelector('[data-rod-readmore-btn]');
  const label = wrap.querySelector('[data-rod-readmore-label]');
  if (!body || !btn) return;
  const overflows = () => {
    body.classList.remove('is-clamped');
    const full = body.scrollHeight;
    body.classList.add('is-clamped');
    return full > body.clientHeight + 4;
  };
  const check = () => {
    const expanded = btn.getAttribute('aria-expanded') === 'true';
    const overflow = overflows();
    body.classList.toggle('is-clamped', !expanded && overflow);
    btn.hidden = !overflow;
  };
  check();
  recheckWhenSettled(check);
  window.addEventListener('resize', Rod.debounce(check, 200));
  btn.addEventListener('click', () => {
    const open = btn.getAttribute('aria-expanded') === 'true';
    btn.setAttribute('aria-expanded', String(!open));
    if (label) label.textContent = open ? 'Devamını oku' : 'Daha az göster';
    check();
  });
}

/** Re-measures once the web fonts are in and once everything has loaded: the first pass runs on a
    cold cache with fallback metrics, which made two-line text look like three lines. */
function recheckWhenSettled(fn) {
  window.addEventListener('load', fn, { once: true });
  if (doc.fonts && doc.fonts.ready) doc.fonts.ready.then(fn).catch(() => {});
  setTimeout(fn, 1200);
}

/* ------------------------------------------------------------------ brand index (/collections) */

function initBrandIndex(list) {
  const items = [...list.children];
  if (items.length < 2) return;
  items
    .slice()
    .sort((a, b) => Rod.collator.compare(a.dataset.rodBrandName || '', b.dataset.rodBrandName || ''))
    .forEach((li) => list.appendChild(li));
}

/* ------------------------------------------------------------------ scroll memory (T-PLP-15)

   Belt and braces for back/forward on a listing. The browser's own restoration is what should do
   this (`history.scrollRestoration` is 'auto'), and it does when the previous page is another
   listing - but coming back from a product page it drops the offset and the shopper lands near the
   top of the results. This remembers the offset of the listings this tab has scrolled and puts it
   back on a `back_forward` navigation, unless the browser already did or the visitor has started
   scrolling themselves. It never runs on a normal (forward) navigation and never fights a #fragment. */

const SCROLL_KEY = 'rod:plp:scroll';
const SCROLL_MAX = 8;

function initScrollMemory() {
  const save = () => {
    const y = Math.round(window.scrollY);
    const url = location.href;
    const list = (Rod.session.get(SCROLL_KEY, []) || []).filter((e) => e && e.u !== url);
    list.unshift({ u: url, y });
    Rod.session.set(SCROLL_KEY, list.slice(0, SCROLL_MAX));
  };
  const saveSoon = Rod.debounce(save, 250);
  window.addEventListener('scroll', saveSoon, { passive: true });
  window.addEventListener('pagehide', save);
  doc.addEventListener('visibilitychange', () => {
    if (doc.visibilityState === 'hidden') save();
  });

  let navType = '';
  try {
    const nav = performance.getEntriesByType('navigation')[0];
    navType = (nav && nav.type) || '';
  } catch (e) {
    navType = '';
  }
  if (navType !== 'back_forward' || location.hash) return;

  const saved = (Rod.session.get(SCROLL_KEY, []) || []).find((e) => e && e.u === location.href);
  const target = saved ? Number(saved.y) : 0;
  if (!(target > 0)) return;

  let cancelled = false;
  const stop = () => {
    cancelled = true;
  };
  for (const ev of ['wheel', 'touchstart', 'keydown', 'pointerdown']) {
    window.addEventListener(ev, stop, { passive: true, once: true });
  }
  const started = Date.now();
  const tryRestore = () => {
    if (cancelled) return;
    if (Math.abs(window.scrollY - target) <= 8) return;
    if (doc.documentElement.scrollHeight - window.innerHeight >= target) {
      window.scrollTo(0, target);
      return;
    }
    if (Date.now() - started < 2500) setTimeout(tryRestore, 100);
  };
  // A short grace period first: when the browser restores by itself there is nothing to do, and
  // this way the page never jumps to one offset and then to another.
  setTimeout(tryRestore, 250);
  window.addEventListener(
    'load',
    () => {
      if (!cancelled) tryRestore();
    },
    { once: true }
  );
}

/* ------------------------------------------------------------------ boot */

function boot() {
  for (const root of doc.querySelectorAll('[data-rod-plp]')) initPlp(root);
  for (const wrap of doc.querySelectorAll('[data-rod-readmore]')) initReadMore(wrap);
  for (const list of doc.querySelectorAll('[data-rod-brand-index]')) initBrandIndex(list);
}

syncViewButtons();
if (doc.querySelector('[data-rod-plp]')) initScrollMemory();

if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', boot, { once: true });
else boot();
